import React, { useState, useRef } from "react";

import { Section } from "~/components/elements/Section";
import { Link } from "gatsby";
import Image from "~/components/elements/Image";
import { Icon } from "~/components/elements/Icon";

const POSTS_PER_PAGE = 20;

const typeNameMap = {
	article: "Article",
	whitepaper: "Whitepaper",
	video: "Video",
	podcast: "Podcast",
	casestudy: "Case Study",
};

const AllListings = (props) => {
	const { posts } = props;

	const listingsRef = useRef(null);

	// Pagination
	const [currentPage, setCurrentPage] = useState(1);

	const indexOfLastPost = currentPage * POSTS_PER_PAGE;
	const indexOfFirstPost = indexOfLastPost - POSTS_PER_PAGE;
	const currentPosts = posts.slice(indexOfFirstPost, indexOfLastPost);
	const totalPages = Math.ceil(posts.length / POSTS_PER_PAGE);

	const handlePageChange = (pageNumber) => {
		setCurrentPage(pageNumber);

		requestAnimationFrame(() => {
			if (listingsRef.current) {
				listingsRef.current.scrollIntoView({
					behavior: "smooth",
					block: "start",
				});
			} else {
				console.error("listingsRef is not available");
			}
		});
	};

	return (
		<Section paddingTop="144" paddingBottom="144" backgroundColour="dark-purple">
			<div className="container">
				{/* Listings */}
				<div ref={listingsRef} style={{ scrollMarginTop: "200px" }}>
					{/* Heading */}
					<h2 className="text-h2 mb-14 text-white">All posts</h2>
					{/* Grid listings */}
					<div className="mt-12 grid grid-cols-1 gap-x-6 gap-y-12 border-t border-t-purple pt-12 sm:grid-cols-2 md:grid-cols-3">
						{posts.map((post, i) => (
							<Link key={`all-listing-post-${post.uri}`} to={post.uri} className="group space-y-5 md:space-y-8">
								{/* Image */}
								<div className="aspect-h-3 aspect-w-4 overflow-hidden">
									<Image
										image={post?.article?.image}
										className="!absolute transform transition-transform duration-700 ease-in-out group-hover:scale-110"
										objectFit="cover"
									/>
								</div>

								<div className="space-y-1 transition-opacity duration-300 ease-in-out group-hover:opacity-60 md:space-y-3.5">
									{/* Type */}
									<div className="text-sm capitalize text-purple">{typeNameMap[post?.article?.type]}</div>

									<div className="text-20 !font-medium leading-[1.25]">
										{/* Post title */}
										{post.title}

										{/* Icon */}
										<div className="ml-3 inline-block">
											<Icon type="arrowRightDown" />
										</div>
									</div>
								</div>
							</Link>
						))}
					</div>

					{/* Pagination */}
					{totalPages > 1 && (
						<div className="mt-12 flex items-center justify-center space-x-4">
							<button
								onClick={() => handlePageChange(currentPage - 1)}
								disabled={currentPage === 1}
								className="text-purple transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
							>
								<PaginationArrow flip />
							</button>

							<div className="flex items-center space-x-2">
								{[...Array(totalPages)].map((_, index) => (
									<button
										key={index + 1}
										onClick={() => handlePageChange(index + 1)}
										className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors
											${currentPage === index + 1 ? "bg-purple text-white" : "text-purple hover:text-white"}`}
									>
										{index + 1}
									</button>
								))}
							</div>

							<button
								onClick={() => handlePageChange(currentPage + 1)}
								disabled={currentPage === totalPages}
								className="text-purple transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
							>
								<PaginationArrow />
							</button>
						</div>
					)}
				</div>
			</div>
		</Section>
	);
};

const PaginationArrow = ({ flip = false }) => {
	return (
		<svg className={`${flip ? "rotate-180" : ""}`} width="26" height="21" viewBox="0 0 26 21" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M14.3434 0.717131L14.3632 0.697848C14.9342 0.188381 15.8177 0.214791 16.3564 0.755706L25.2948 9.46231C25.8387 9.9941 25.8397 10.8537 25.2969 11.3865L16.3534 20.098C15.8147 20.6389 14.9312 20.6653 14.3602 20.1559C14.0893 19.9022 13.9362 19.5541 13.932 19.1887C13.9278 18.8233 14.0758 18.4722 14.3404 18.2144L19.6125 13.0791C19.8354 12.8619 19.9032 12.5341 19.7823 12.25C19.6615 11.9658 19.376 11.7811 19.0603 11.7821L1.67955 11.78C1.30552 11.781 0.946063 11.6339 0.684568 11.3731C0.422012 11.1133 0.277192 10.7611 0.284483 10.3968C0.317824 9.64173 0.96172 9.05107 1.73584 9.06222L19.0656 9.05729C19.3824 9.05729 19.6679 8.87257 19.7877 8.58741C19.9085 8.30324 19.8408 7.97546 19.6168 7.75928L14.3469 2.62604C13.8103 2.09731 13.8082 1.24684 14.3437 0.717068L14.3434 0.717131Z"
				fill="currentColor"
			/>
		</svg>
	);
};

export default AllListings;
